import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { LoadingSmall } from "../LoadingSmall";
import { AppMultiSelectCommand } from "./app-multi-select-command";

interface Option {
  value: string | number;
  label: string;
  icon?: React.ReactNode;
}

export interface AppMultiSelectProps {
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  options: Option[];
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
  colorScheme?: 'blue' | 'purple' | 'green' | 'amber' | 'default';
}

export const AppMultiSelect: React.FC<AppMultiSelectProps> = ({
  value = [],
  onChange,
  options,
  isLoading,
  placeholder,
  label,
  colorScheme = 'default'
}) => {
  const [open, setOpen] = React.useState(false);

  const selectedOptions = options.filter((option) => value.includes(option.value));

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length <= 2) return selectedOptions.map((option) => option.label).join(", ");
    return `${selectedOptions
      .slice(0, 2)
      .map((option) => option.label)
      .join(", ")} +${value.length - 2}`;
  };

  // Color scheme styles
  const getColorStyles = () => {
    switch (colorScheme) {
      case 'blue':
        return {
          button: "border-blue-200 hover:border-blue-300 focus:ring-blue-500",
          icon: "text-blue-500"
        };
      case 'purple':
        return {
          button: "border-purple-200 hover:border-purple-300 focus:ring-purple-500",
          icon: "text-purple-500"
        };
      case 'green':
        return {
          button: "border-green-200 hover:border-green-300 focus:ring-green-500",
          icon: "text-green-500"
        };
      case 'amber':
        return {
          button: "border-amber-200 hover:border-amber-300 focus:ring-amber-500",
          icon: "text-amber-500"
        };
      default:
        return {
          button: "",
          icon: ""
        };
    }
  };

  const colorStyles = getColorStyles();

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between truncate", 
            value.length > 0 && "text-foreground",
            colorStyles.button
          )}
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingSmall />
          ) : (
            <>
              <span className="truncate">{getDisplayText()}</span>
              <ChevronsUpDown className={cn("ml-2 h-4 w-4 shrink-0 opacity-50", colorScheme !== 'default' && colorStyles.icon)} />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <AppMultiSelectCommand
          value={value}
          onChange={onChange}
          options={options}
          isLoading={isLoading}
          placeholder={placeholder}
          label={label}
          colorScheme={colorScheme}
        />
      </PopoverContent>
    </Popover>
  );
};
